/**
 * Element-scoped operations: what the board sends, and what it shows meanwhile.
 *
 * The server is the authority on whether an operation is legal — the same
 * applier runs for the therapist and for the patient, which is what keeps the
 * two paths from drifting. What lives here is the other half: building the
 * operations, folding a drag's worth of them into one, and showing the result
 * immediately so the board does not wait a round trip to move a figure.
 */

import type {
    Household,
    MapLink,
    MapNode,
    MapState,
} from "./types";

export type OperationName =
    | "upsert_node"
    | "move_node"
    | "remove_node"
    | "upsert_link"
    | "remove_link"
    | "upsert_household"
    | "remove_household"
    | "set_meta";

/**
 * One operation's payload.
 *
 * Left open rather than eight discriminated shapes: the backend validates a
 * single open payload through one applier, and mirroring it as a union here
 * would be a second rule set to keep in step — the one that drifts is the one
 * that lets something through.
 */
export type OperationPayload = Record<string, unknown>;

export interface Operation {
    op: OperationName;
    payload: OperationPayload;
}

export const upsertNode = (node: MapNode): Operation => ({
    op: "upsert_node",
    payload: { ...node },
});

export const moveNode = (id: string, x: number, y: number): Operation => ({
    op: "move_node",
    payload: { id, x, y },
});

export const removeNode = (id: string): Operation => ({
    op: "remove_node",
    payload: { id },
});

export const upsertLink = (link: MapLink): Operation => ({
    op: "upsert_link",
    payload: { ...link },
});

export const removeLink = (id: string): Operation => ({
    op: "remove_link",
    payload: { id },
});

export const upsertHousehold = (household: Household): Operation => ({
    op: "upsert_household",
    payload: { ...household },
});

export const removeHousehold = (id: string): Operation => ({
    op: "remove_household",
    payload: { id },
});

export const setMeta = (meta: Partial<MapState["meta"]>): Operation => ({
    op: "set_meta",
    payload: { ...meta },
});

const isMove = (operation: Operation) => operation.op === "move_node";
const targetOf = (operation: Operation): string | undefined =>
    typeof operation.payload.id === "string" ? operation.payload.id : undefined;

/**
 * Fold a queue before sending it.
 *
 * A drag emits a move per animation frame. Sending all of them would spend the
 * batch limit on positions nobody will ever see, and make the other side
 * replay the whole drag. Only the last position of an uninterrupted run
 * matters — interrupted by anything else touching that element, because
 * reordering an edit behind a move changes what the other side ends up with.
 */
export const collapseOperations = (operations: Operation[]): Operation[] => {
    const collapsed: Operation[] = [];
    const openMove = new Map<string, number>();

    for (const operation of operations) {
        const target = targetOf(operation);

        if (isMove(operation) && target) {
            const existing = openMove.get(target);
            if (existing !== undefined) {
                collapsed[existing] = operation; // keep the position, keep the place
                continue;
            }
            openMove.set(target, collapsed.length);
            collapsed.push(operation);
            continue;
        }

        // Anything else touching this element closes its run, so a later move
        // is a new one rather than folded into the earlier position.
        if (target) openMove.delete(target);
        collapsed.push(operation);
    }

    return dropMovesOfRemoved(collapsed);
};

/** A move of something this same batch removes is noise the server discards. */
const dropMovesOfRemoved = (operations: Operation[]): Operation[] => {
    const removed = new Set(
        operations
            .filter((operation) => operation.op === "remove_node")
            .map(targetOf),
    );
    if (removed.size === 0) return operations;
    return operations.filter(
        (operation) => !(isMove(operation) && removed.has(targetOf(operation))),
    );
};

/**
 * Apply a batch to the board on screen, without waiting for the server.
 *
 * Deliberately not a second validator: it assumes the operations are legal and
 * shows what they would do. The next poll replaces this with the server's
 * document, which is the one that decides. The cascades are mirrored, though —
 * a board that keeps drawing a line to a person who was just removed looks
 * broken in the half-second before the answer arrives.
 */
export const applyLocally = (state: MapState, operations: Operation[]): MapState => {
    let nodes = [...state.nodes];
    let links = [...state.links];
    let households = [...state.households];
    let meta = { ...state.meta };

    const cascadeFrom = (ids: Set<string>) => {
        let changed = true;
        while (changed) {
            const before = links.length;
            links = links.filter((link) => {
                const orphaned = ids.has(link.from) || ids.has(link.to);
                if (orphaned) ids.add(link.id);
                return !orphaned;
            });
            changed = links.length !== before;
        }
    };

    for (const { op, payload } of operations) {
        switch (op) {
            case "upsert_node": {
                const node = payload as unknown as MapNode;
                const at = nodes.findIndex((existing) => existing.id === node.id);
                nodes = at === -1
                    ? [...nodes, node]
                    : nodes.map((existing, index) => (index === at ? node : existing));
                break;
            }
            case "move_node": {
                nodes = nodes.map((node) =>
                    node.id === payload.id
                        ? { ...node, x: payload.x as number, y: payload.y as number }
                        : node,
                );
                break;
            }
            case "remove_node": {
                nodes = nodes.filter((node) => node.id !== payload.id);
                cascadeFrom(new Set<string>([payload.id as string]));
                households = households.map((household) => ({
                    ...household,
                    members: household.members.filter((member) => member !== payload.id),
                }));
                break;
            }
            case "upsert_link": {
                const link = payload as unknown as MapLink;
                const at = links.findIndex((existing) => existing.id === link.id);
                links = at === -1
                    ? [...links, link]
                    : links.map((existing, index) => (index === at ? link : existing));
                break;
            }
            case "remove_link": {
                links = links.filter((link) => link.id !== payload.id);
                cascadeFrom(new Set<string>([payload.id as string]));
                break;
            }
            case "upsert_household": {
                const household = payload as unknown as Household;
                const at = households.findIndex((existing) => existing.id === household.id);
                households = at === -1
                    ? [...households, household]
                    : households.map((existing, index) =>
                          index === at ? household : existing,
                      );
                break;
            }
            case "remove_household": {
                households = households.filter((household) => household.id !== payload.id);
                break;
            }
            case "set_meta": {
                meta = { ...meta, ...payload };
                break;
            }
        }
    }

    return { ...state, nodes, links, households, meta };
};
